import {
  Alert,
  AlertTitle,
  Button,
  Heading,
  Input,
  NavigationTabs,
  NavigationTabsItem,
  Paragraph,
  Stack,
  Text,
} from '@sixfold/common-ui';
import React, { useState } from 'react';
import { Navigate, Route, Routes as RouterRoutes, useNavigate } from 'react-router';

import { CompaniesTable } from './companies_table';
import { CompanyPage } from './company_page';
import { Insights } from './insights';
import { TourPage } from './tour_page';
import { ToursTable, toursViewSearch } from './tours_table';
import { LoadingState, StatCard } from './ui';
import { useTimeZone } from '../../components/date_formatting/timezone_context';
import { formatDateWithTimeZone } from '../../lib/date';
import { Routes } from '../../routes';
import { MobileDataContext, STATS_WINDOW_DAYS, useMobileData, useMobileDataSource } from '../hooks';

import styles from './mobile.module.css';

const TourJump: React.FC = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = value.trim();
    if (trimmed === '') return;
    void navigate(`${Routes.MobileTours.generatePath({})}?q=${encodeURIComponent(trimmed)}`);
  };
  return (
    <form onSubmit={submit}>
      <Stack alignItems="end" gap="small" wrap={false}>
        <Input
          label="Open a tour"
          name="mobile-tour-jump"
          icon="small-search"
          placeholder="Tour ID or transport number"
          value={value}
          onChange={({ value: next }) => setValue(next)}
          maxWidth={320}
        />
        <Button type="submit" kind="primary">
          Open
        </Button>
      </Stack>
    </form>
  );
};

const Overview: React.FC = () => {
  const { companies, statsByCompany, totals, dataAsOf, loading, error } = useMobileData();
  const { timeZone } = useTimeZone();
  const activeCompanies = statsByCompany.size;
  const percent = (part: number, total: number) => (total === 0 ? '0%' : `${Math.round((part / total) * 100)}%`);
  const toursPath = Routes.MobileTours.generatePath({});

  return (
    <Stack direction="column" wrap={false} gap="large">
      <Stack justifyContent="space-between" alignItems="end" gap="medium">
        <Stack direction="column" wrap={false} gap="xsmall">
          <Heading type="title" size="large">
            Mobile Command Center
          </Heading>
          <Text prominence="subtle">
            Troubleshoot Visibility Drive transports: allocation, driver login, app tracking and positions in one place.
            {dataAsOf === null ? '' : ` Overview data as of ${formatDateWithTimeZone(dataAsOf, timeZone)}.`}
          </Text>
        </Stack>
        <TourJump />
      </Stack>

      {error !== undefined && (
        <Alert kind="danger">
          <AlertTitle>Could not load mobile data</AlertTitle>
          <Paragraph>{error.message}</Paragraph>
        </Alert>
      )}

      <div className={styles.kpiGrid}>
        <StatCard
          label="Mobile transports"
          value={totals.mobile}
          loading={loading}
          hint={`Created in the last ${STATS_WINDOW_DAYS} days`}
          pathname={toursPath}
        />
        <StatCard
          label="Completed"
          value={totals.completed}
          loading={loading}
          tone="success"
          pathname={toursPath}
          search={toursViewSearch({ status: 'completed' })}
        />
        <StatCard
          label="Failed"
          value={totals.failed}
          loading={loading}
          tone={totals.failed > 0 ? 'danger' : 'default'}
          pathname={toursPath}
          search={toursViewSearch({ status: 'failed' })}
        />
        <StatCard
          label="Ongoing now"
          value={totals.ongoing}
          loading={loading}
          pathname={toursPath}
          search={toursViewSearch({ status: 'ongoing' })}
        />
        <StatCard
          label="Needs action"
          value={totals.needsAction}
          loading={loading}
          tone={totals.needsAction > 0 ? 'danger' : 'success'}
          hint="Failing tours or tours with warnings"
          pathname={toursPath}
          search={toursViewSearch({ health: 'action' })}
        />
        <StatCard
          label="Positions received"
          value={percent(totals.tracked, totals.mobile)}
          loading={loading}
          hint={`${totals.tracked} of ${totals.mobile} mobile transports`}
          pathname={toursPath}
          search={toursViewSearch({ positions: 'received' })}
        />
        <StatCard
          label="Companies with mobile transports"
          value={activeCompanies}
          loading={loading}
          hint={`Last ${STATS_WINDOW_DAYS} days`}
          pathname={Routes.MobileCompanies.generatePath({})}
        />
      </div>

      <NavigationTabs>
        <NavigationTabsItem href={Routes.MobileTours.generatePath({})}>
          Tours{loading ? '' : ` (${totals.mobile})`}
        </NavigationTabsItem>
        <NavigationTabsItem href={Routes.MobileCompanies.generatePath({})}>
          Companies{loading ? '' : ` (${activeCompanies})`}
        </NavigationTabsItem>
        <NavigationTabsItem href={Routes.MobileInsights.generatePath({})}>Insights</NavigationTabsItem>
      </NavigationTabs>

      <RouterRoutes>
        <Route path={Routes.MobileTours.relativePath} element={<ToursTable />} />
        <Route
          path={Routes.MobileCompanies.relativePath}
          element={
            loading ? (
              <LoadingState label="Loading mobile companies" />
            ) : (
              <CompaniesTable companies={companies} statsByCompany={statsByCompany} />
            )
          }
        />
        <Route path={Routes.MobileInsights.relativePath} element={<Insights />} />
        <Route path="*" element={<Navigate to={Routes.MobileTours.generatePath({})} replace />} />
      </RouterRoutes>
    </Stack>
  );
};

export const MobileCommandCenter: React.FC = () => {
  const data = useMobileDataSource();
  return (
    <MobileDataContext.Provider value={data}>
      <div className={styles.page}>
        <RouterRoutes>
          <Route path={Routes.MobileTour.relativePath} element={<TourPage />} />
          <Route path={Routes.MobileCompany.relativePath} element={<CompanyPage />} />
          <Route path="*" element={<Overview />} />
        </RouterRoutes>
      </div>
    </MobileDataContext.Provider>
  );
};
